import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import type {
  ConnectMangopayLegalRepresentative,
  ConnectMangopayServiceProvider,
} from '@pflegenavi/service-provider-frontend/api';
import {
  useAcceptInvitationPhoenix,
  useConnectServiceProviderMangopayAccount,
  useEnrollScaEnrollment,
  useGetAllCountriesPhoenix,
  useGetNursingHomeList,
  useGetServiceProviderPhoenix,
  useServiceProvider,
  useSortedCountries,
  useUserProfile,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { sanitizeString } from '@pflegenavi/shared/utils';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Grid, Stack } from '@mui/material';
import { WelcomeSideBar } from '../components/general/WelcomeSideBar';
import { ConnectionStatus } from '@pflegenavi/service-provider-shared/api-endpoints';
import { AlreadyAcceptedInfo } from '../components/general/AlreadyAcceptedInfo';
import { MangopayConnectForm } from '../components/connect-account/MangopayConnectForm/MangopayConnectForm';
import { ScaEnrollmentWaitingScreen } from '../components/connect-account/ScaEnrollmentWaitingScreen';
import { useErrorSnackbar } from '@pflegenavi/web-components';
import { keepPreviousData } from '@tanstack/react-query';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface NewInvitationPageProps {}

export const ConnectMangopayAccountPage: FC<NewInvitationPageProps> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Parameters are optional - only needed for invitation handling
  const nursingHomeId = searchParams.get('nursingHomeId');
  const globalServiceProviderId = searchParams.get('globalServiceProviderId');

  const { user } = useAuthentication();
  const userId = user?.userId;

  const { data: nursingHomeList } = useGetNursingHomeList();

  const { data: userProfile, isPending: isUserProfileLoading } = useUserProfile(
    {}
  );
  const {
    data: serviceProviderProfile,
    isLoading: isServiceProviderProfileLoading,
  } = useGetServiceProviderPhoenix();

  const {
    mutateAsync: acceptInvitation,
    isPending: isAcceptInvitationLoading,
  } = useAcceptInvitationPhoenix();

  const { data: serviceProvider } = useServiceProvider(
    {
      globalServiceProviderId: globalServiceProviderId ?? undefined,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const { mutateAsync: connectAccount, isPending: isConnectAccountLoading } =
    useConnectServiceProviderMangopayAccount();

  const { mutateAsync: enrollSca, isPending: isEnrollScaLoading } =
    useEnrollScaEnrollment();

  const { data: countriesData, isPending: areCountriesLoading } =
    useGetAllCountriesPhoenix({
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    });

  const countries = useSortedCountries(countriesData ?? []);

  // The nursing home is only available if we have nursingHomeId and it exists in the list
  // MangoPay setup can happen without a nursing home context
  const nursingHome = useMemo(() => {
    if (!nursingHomeId) {
      return null;
    }
    return nursingHomeList?.find(
      (nursingHome) => nursingHome.nursingHomeId === nursingHomeId
    );
  }, [nursingHomeList, nursingHomeId]);

  const userDetails = useMemo(() => {
    return {
      firstName:
        userProfile?.user.firstName &&
        userProfile.user.firstName !== userProfile.user.eMail // The endpoint returns the email address also in the firstName field if no firstName is set
          ? userProfile.user.firstName
          : '',
      lastName: userProfile?.user.lastName ?? '',
      email: userProfile?.user?.eMail ?? '',
    };
  }, [userProfile]);

  const companyDetails = useMemo(() => {
    return {
      name: serviceProvider?.name ?? '',
      address: serviceProvider?.address ?? '',
      city: serviceProvider?.city ?? '',
      zipCode: serviceProvider?.zipCode ?? '',
      phone: serviceProvider?.phone ?? '',
      countryId: serviceProvider?.countryId ?? '',
    };
  }, [serviceProvider]);

  const connectionStatus = nursingHome?.connectionStatus;
  const accountAffiliationName = nursingHome?.nursingHomeName;

  // Check if MangoPay user exists but SCA not enrolled (matches mobile's onboarding-check logic)
  // In this case, show the SCA waiting screen instead of the full onboarding form
  const needsScaEnrollmentOnly =
    serviceProviderProfile?.data?.mangopay_user_id &&
    !serviceProviderProfile?.data?.mangopay_sca_enrolled;

  const handleConnectAccount = useCallback(
    async (
      service_provider: ConnectMangopayServiceProvider,
      legal_representative: ConnectMangopayLegalRepresentative
    ) => {
      try {
        if (!userId) {
          throw new Error('Missing user id');
        }

        const response = await connectAccount({
          name: sanitizeString(service_provider.name),
          company_number: sanitizeString(service_provider.company_number),
          legal_person_type: service_provider.legal_person_type,
          email: sanitizeString(service_provider.email),
          phone: sanitizeString(service_provider.phone),
          address: sanitizeString(
            service_provider.headquarters_address.address
          ),
          city: sanitizeString(service_provider.headquarters_address.city),
          zip_code: sanitizeString(
            service_provider.headquarters_address.zip_code
          ),
          country_id: service_provider.headquarters_address.country_id,
          legal_representative_base_data: {
            first_name: sanitizeString(legal_representative.first_name),
            last_name: sanitizeString(legal_representative.last_name),
            birth_date: legal_representative.birth_date,
            nationality_id: legal_representative.nationality_id,
            country_of_residence_id:
              legal_representative.country_of_residence_id,
          },
        });

        // Auto-accept invitation after successful account creation
        if (nursingHome) {
          await acceptInvitation({
            nursing_home_id: nursingHomeId as string,
          });
        }

        // Check for SCA redirect URL (SOLETRADER)
        if (response.redirect_url) {
          // Add returnUrl query parameter for success/failure handling
          const returnUrl = `${window.location.origin}${spAppMainPages.SETUP_BANK_ACCOUNT}`;

          // Check if the redirect URL already has query parameters
          const separator = response.redirect_url.includes('?') ? '&' : '?';
          const redirectUrl = `${
            response.redirect_url
          }${separator}returnUrl=${encodeURIComponent(returnUrl)}`;

          // Redirect to MangoPay SCA verification
          window.location.href = redirectUrl;
          return;
        }

        // If no redirect URL from user creation, proactively enroll in SCA (all user types)
        try {
          const returnUrl = `${window.location.origin}${spAppMainPages.SETUP_BANK_ACCOUNT}`;

          const enrollmentResponse = await enrollSca(returnUrl);

          if (enrollmentResponse.redirect_url) {
            const separator = enrollmentResponse.redirect_url.includes('?')
              ? '&'
              : '?';
            const redirectUrl = `${
              enrollmentResponse.redirect_url
            }${separator}returnUrl=${encodeURIComponent(returnUrl)}`;

            // Redirect to MangoPay SCA verification
            window.location.href = redirectUrl;
            return;
          }
        } catch (error) {
          enqueueErrorSnackbar(
            error,
            t('service-provider-app.mangopay.sca-enrollment-failed')
          );
          // Continue to next step even if SCA enrollment fails
        }

        // Normal flow - navigate to bank account setup
        // If the nursing home is not there, it either does not exist (unlikely) or the invitation is not valid anymore.
        // In that case, the user can still set up their account details, but is not affiliated with a nursing home (but
        // may be invited by one later on).
        enqueueSnackbar(t('service-provider-app.connect-account.data-saved'), {
          variant: 'success',
        });

        // Navigate to bank account setup page
        navigate(spAppMainPages.SETUP_BANK_ACCOUNT);
      } catch (error) {
        enqueueErrorSnackbar(
          error,
          t('service-provider-app.connect-account.failed-to-update-profile')
        );
      }
    },
    [
      userId,
      connectAccount,
      enrollSca,
      enqueueSnackbar,
      enqueueErrorSnackbar,
      t,
      navigate,
      acceptInvitation,
      nursingHome,
      nursingHomeId,
    ]
  );

  const loading = useMemo(() => {
    return (
      isUserProfileLoading ||
      areCountriesLoading ||
      isConnectAccountLoading ||
      isAcceptInvitationLoading ||
      isServiceProviderProfileLoading ||
      isEnrollScaLoading
    );
  }, [
    areCountriesLoading,
    isUserProfileLoading,
    isConnectAccountLoading,
    isAcceptInvitationLoading,
    isServiceProviderProfileLoading,
    isEnrollScaLoading,
  ]);

  if (
    isUserProfileLoading ||
    areCountriesLoading ||
    isServiceProviderProfileLoading
  ) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          height: '100%',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
        }}
      >
        <CircularProgress color="primary" variant="indeterminate" />
      </Stack>
    );
  }

  // SCA-only flow: User has MangoPay account but needs SCA enrollment
  // Show the SCA waiting screen instead of the full onboarding form
  if (needsScaEnrollmentOnly) {
    return <ScaEnrollmentWaitingScreen />;
  }

  return (
    <Grid container height="100vh" overflow="hidden">
      <Grid item md={3} height="100vh">
        <WelcomeSideBar
          title={t('service-provider-app.connect-account.hello')}
          subtitle={t(
            'service-provider-app.connect-account.welcome-to-pflegenavi'
          )}
        />
      </Grid>
      <Grid
        item
        md={9}
        height="100vh"
        overflow="auto"
        position="relative"
        pb={10}
      >
        {connectionStatus &&
        connectionStatus !== ConnectionStatus.InvitePending &&
        accountAffiliationName ? (
          <AlreadyAcceptedInfo
            connectionStatus={connectionStatus}
            accountAffiliationName={accountAffiliationName}
          />
        ) : (
          // User needs to create MangoPay account
          <MangopayConnectForm
            loading={loading}
            companyDetails={companyDetails}
            userDetails={userDetails}
            countryOptions={countries ?? []}
            accountAffiliationName={accountAffiliationName}
            hasNursingHomeIdInUrl={Boolean(nursingHomeId)}
            handleConnectAccount={handleConnectAccount}
            mangopayUserAccountConnected={false}
          />
        )}
      </Grid>
    </Grid>
  );
};
